<template>
  <div class="users-wrapper">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1>Users</h1>
        <p>Manage registered users and their account information.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add User
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-group"></i>
        </div>
        <div>
          <span>Total Users</span>
          <strong>{{ users.length }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-check-circle"></i>
        </div>
        <div>
          <span>Verified Users</span>
          <strong>{{ verifiedUsers }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-user-check"></i>
        </div>
        <div>
          <span>Creators</span>
          <strong>{{ creatorUsers }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-calendar"></i>
        </div>
        <div>
          <span>New This Month</span>
          <strong>{{ newUsersThisMonth }}</strong>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="content-card">
      <div class="toolbar">
        <div class="search-box">
          <i class="bx bx-search"></i>
          <input
            v-model="search"
            type="text"
            placeholder="Search by name, email or mobile..."
          />
        </div>

        <select v-model="verificationFilter" class="filter-select">
          <option value="">All Users</option>
          <option value="verified">Verified</option>
          <option value="unverified">Unverified</option>
        </select>

        <select v-model="signupFilter" class="filter-select">
          <option value="">All Signup Modes</option>
          <option value="Email">Email</option>
          <option value="Mobile">Mobile</option>
          <option value="Google">Google</option>
        </select>

        <select v-model="cityFilter" class="filter-select">
          <option value="">All Cities</option>
          <option
            v-for="city in cities"
            :key="city"
            :value="city"
          >
            {{ city }}
          </option>
        </select>

        <button class="reset-btn" @click="resetFilters">
          <i class="bx bx-reset"></i>
          Reset
        </button>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Contact</th>
              <th>Signup Mode</th>
              <th>Location</th>
              <th>Verified</th>
              <th>Joined</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="paginatedUsers.length === 0">
              <td colspan="7" class="empty-state">
                <i class="bx bx-user-x"></i>
                <h3>No users found</h3>
                <p>Try changing your search or filters.</p>
              </td>
            </tr>

            <tr
              v-for="user in paginatedUsers"
              :key="user.user_id"
            >
              <!-- User -->
              <td>
                <div class="user-info">
                  <div class="avatar">
                    <img
                      v-if="user.image"
                      :src="user.image"
                      :alt="user.name"
                    />
                    <span v-else>
                      {{ getInitials(user.name) }}
                    </span>
                  </div>

                  <div class="user-details">
                    <strong>{{ user.name }}</strong>
                    <small>#{{ user.user_id }}</small>
                  </div>
                </div>
              </td>

              <!-- Contact -->
              <td>
                <div class="contact-info">
                  <span v-if="user.email">
                    <i class="bx bx-envelope"></i>
                    {{ user.email }}
                  </span>

                  <span v-if="user.mobile">
                    <i class="bx bx-phone"></i>
                    {{ user.mobile }}
                  </span>
                </div>
              </td>

              <!-- Signup -->
              <td>
                <span class="signup-badge">
                  {{ user.signup_mode || "—" }}
                </span>
              </td>

              <!-- Location -->
              <td>
                <div class="location-info">
                  <strong>{{ user.city || "—" }}</strong>
                  <small>
                    {{ user.state || "" }}
                    <span v-if="user.country">, {{ user.country }}</span>
                  </small>
                </div>
              </td>

              <!-- Verification -->
              <td>
                <span
                  class="status-badge"
                  :class="user.isVerified ? 'verified' : 'unverified'"
                >
                  <i
                    class="bx"
                    :class="
                      user.isVerified
                        ? 'bx-check-circle'
                        : 'bx-time-five'
                    "
                  ></i>

                  {{ user.isVerified ? "Verified" : "Unverified" }}
                </span>
              </td>

              <!-- Joined -->
              <td>
                <span class="date-text">
                  {{ formatDate(user.user_cre_datetime) }}
                </span>
              </td>

              <!-- Actions -->
              <td>
                <div class="actions">
                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewUser(user)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editUser(user)"
                  >
                    <i class="bx bx-edit-alt"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(user)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="totalPages > 1">
        <span>
          Showing {{ startItem }}–{{ endItem }} of {{ filteredUsers.length }}
        </span>

        <div class="pagination-buttons">
          <button
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>

          <button
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <i class="bx bx-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>{{ isEditing ? "Edit User" : "Add User" }}</h2>
            <p>
              {{
                isEditing
                  ? "Update user account information."
                  : "Create a new user account."
              }}
            </p>
          </div>

          <button class="close-btn" @click="closeFormModal">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="profile-upload">
            <div class="large-avatar">
              <img
                v-if="form.image"
                :src="form.image"
                :alt="form.name"
              />
              <span v-else>
                {{ getInitials(form.name || "User") }}
              </span>
            </div>

            <div>
              <strong>Profile Image</strong>
              <p>Enter an image URL for the user profile.</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group full">
              <label>Image URL</label>
              <input
                v-model="form.image"
                type="url"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div class="form-group">
              <label>Name <span>*</span></label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Enter user name"
              />
            </div>

            <div class="form-group">
              <label>Email</label>
              <input
                v-model="form.email"
                type="email"
                placeholder="Enter email"
              />
            </div>

            <div class="form-group">
              <label>Mobile</label>
              <input
                v-model.trim="form.mobile"
                type="tel"
                pattern="[0-9]{7,15}"
                placeholder="Enter mobile number"
              />
            </div>

            <div class="form-group">
              <label>Signup Mode</label>
              <select v-model="form.signup_mode">
                <option value="">Select Mode</option>
                <option value="Email">Email</option>
                <option value="Mobile">Mobile</option>
                <option value="Google">Google</option>
              </select>
            </div>

            <div class="form-group">
              <label>Country</label>

              <select v-model="form.country">
                <option value="">Select Country</option>

                <option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.name"
                >
                  {{ country.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>State</label>

              <select
                v-model="form.state"
                :disabled="!form.country"
              >
                <option value="">Select State</option>

                <option
                  v-for="state in formStates"
                  :key="state.code"
                  :value="state.name"
                >
                  {{ state.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>City</label>

              <select
                v-model="form.city"
                :disabled="!form.state"
              >
                <option value="">Select City</option>

                <option
                  v-for="city in formCities"
                  :key="city"
                  :value="city"
                >
                  {{ city }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Pincode</label>
              <input
                v-model="form.pincode"
                type="text"
                placeholder="Enter pincode"
              />
            </div>

            <div class="form-group full">
              <label>Address</label>
              <textarea
                v-model="form.address"
                rows="3"
                placeholder="Enter address"
              ></textarea>
            </div>

            <div class="form-group">
              <label>YouTube Channel</label>
              <input
                v-model="form.youtube_channel"
                type="url"
                placeholder="YouTube channel URL"
              />
            </div>

            <div class="form-group">
              <label>Instagram</label>
              <input
                v-model="form.insta_link"
                type="url"
                placeholder="Instagram URL"
              />
            </div>

            <div class="form-group full">
              <label>Portfolio</label>
              <input
                v-model="form.portfolio_link"
                type="url"
                placeholder="Portfolio URL"
              />
            </div>

            <div class="form-group checkbox-group full">
              <label class="checkbox-label">
                <input
                  v-model="form.isVerified"
                  type="checkbox"
                />
                <span>Verified User</span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="secondary-btn" @click="closeFormModal">
            Cancel
          </button>

          <button class="primary-btn" @click="saveUser">
            <i class="bx bx-check"></i>
            {{ isEditing ? "Update User" : "Create User" }}
          </button>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="showViewModal = false"
    >
      <div class="modal view-modal">
        <div class="modal-header">
          <div>
            <h2>User Details</h2>
            <p>Complete user account information.</p>
          </div>

          <button
            class="close-btn"
            @click="showViewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body" v-if="selectedUser">
          <div class="view-profile">
            <div class="large-avatar">
              <img
                v-if="selectedUser.image"
                :src="selectedUser.image"
                :alt="selectedUser.name"
              />
              <span v-else>
                {{ getInitials(selectedUser.name) }}
              </span>
            </div>

            <div>
              <h3>{{ selectedUser.name }}</h3>
              <p>User ID #{{ selectedUser.user_id }}</p>

              <span
                class="status-badge"
                :class="
                  selectedUser.isVerified
                    ? 'verified'
                    : 'unverified'
                "
              >
                <i
                  class="bx"
                  :class="
                    selectedUser.isVerified
                      ? 'bx-check-circle'
                      : 'bx-time-five'
                  "
                ></i>
                {{
                  selectedUser.isVerified
                    ? "Verified User"
                    : "Unverified User"
                }}
              </span>
            </div>
          </div>

          <div class="detail-section">
            <h4>Contact Information</h4>

            <div class="details-grid">
              <div class="detail-item">
                <span>Email</span>
                <strong>{{ selectedUser.email || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>Mobile</span>
                <strong>{{ selectedUser.mobile || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>Signup Mode</span>
                <strong>{{ selectedUser.signup_mode || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>Joined</span>
                <strong>
                  {{ formatDate(selectedUser.user_cre_datetime) }}
                </strong>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Location</h4>

            <div class="details-grid">
              <div class="detail-item">
                <span>Address</span>
                <strong>{{ selectedUser.address || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>City</span>
                <strong>{{ selectedUser.city || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>State</span>
                <strong>{{ selectedUser.state || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>Country</span>
                <strong>{{ selectedUser.country || "—" }}</strong>
              </div>

              <div class="detail-item">
                <span>Pincode</span>
                <strong>{{ selectedUser.pincode || "—" }}</strong>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Social Profiles</h4>

            <div class="social-links">
              <a
                v-if="selectedUser.youtube_channel"
                :href="selectedUser.youtube_channel"
                target="_blank"
              >
                <i class="bx bxl-youtube"></i>
                YouTube
              </a>

              <a
                v-if="selectedUser.insta_link"
                :href="selectedUser.insta_link"
                target="_blank"
              >
                <i class="bx bxl-instagram"></i>
                Instagram
              </a>

              <a
                v-if="selectedUser.portfolio_link"
                :href="selectedUser.portfolio_link"
                target="_blank"
              >
                <i class="bx bx-link"></i>
                Portfolio
              </a>

              <span
                v-if="
                  !selectedUser.youtube_channel &&
                  !selectedUser.insta_link &&
                  !selectedUser.portfolio_link
                "
                class="no-data"
              >
                No social profiles added.
              </span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="showViewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="
              showViewModal = false;
              editUser(selectedUser);
            "
          >
            <i class="bx bx-edit-alt"></i>
            Edit User
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="showDeleteModal = false"
    >
      <div class="delete-modal">
        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h2>Delete User?</h2>

        <p>
          Are you sure you want to delete
          <strong>{{ selectedUser?.name }}</strong>?
          This action cannot be undone.
        </p>

        <div class="delete-actions">
          <button
            class="secondary-btn"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>

          <button class="danger-btn" @click="deleteUser">
            Delete User
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUsersApi,
  getUserStatsApi,
  getUserApi,
  createUserApi,
  updateUserApi,
  deleteUserApi,
} from "@/apis/usersapis";
import countries from "@/other/countries.json";

export default {
  name: "UserScreen",

  data() {
    return {
      countries,
      search: "",
      verificationFilter: "",
      signupFilter: "",
      cityFilter: "",

      currentPage: 1,
      perPage: 8,

      totalUsers: 0,
      totalPages: 0,

      verifiedUsers: 0,
      creatorUsers: 0,
      newUsersThisMonth: 0,

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,
      selectedUser: null,

      users: [],

      form: {
        user_id: null,
        name: "",
        mobile: "",
        email: "",
        image: "",
        isVerified: false,
        signup_mode: "",
        youtube_channel: "",
        insta_link: "",
        portfolio_link: "",
        address: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
      },
    };
  },
computed: {
  paginatedUsers() {
    return this.users;
  },

  filteredUsers() {
    return this.users;
  },

  startItem() {
    if (!this.totalUsers) return 0;

    return (this.currentPage - 1) * this.perPage + 1;
  },

  endItem() {
    return Math.min(
      this.currentPage * this.perPage,
      this.totalUsers
    );
  },

  // ================================
  // FORM STATES
  // ================================

  formStates() {
    if (!this.form.country) {
      return [];
    }

    const country = this.countries.find(
      (item) => item.name === this.form.country
    );

    return country?.states || [];
  },

  // ================================
  // FORM CITIES
  // ================================

  formCities() {
    if (!this.form.country || !this.form.state) {
      return [];
    }

    const country = this.countries.find(
      (item) => item.name === this.form.country
    );

    const state = country?.states?.find(
      (item) => item.name === this.form.state
    );

    return state?.cities || [];
  },

  // ================================
  // CITY FILTER
  // ================================

  cities() {
    return [
      ...new Set(
        this.users
          .map((user) => user.city)
          .filter(Boolean)
      ),
    ].sort();
  },
},

  watch: {
    search() {
      this.currentPage = 1;
      this.loadUsers();
    },

    verificationFilter() {
      this.currentPage = 1;
      this.loadUsers();
    },

    signupFilter() {
      this.currentPage = 1;
      this.loadUsers();
    },
cityFilter() {
    this.currentPage = 1;
    this.loadUsers();
  },

  // ================================
  // COUNTRY CHANGE
  // ================================

  "form.country"() {
    this.form.state = "";
    this.form.city = "";
  },

  // ================================
  // STATE CHANGE
  // ================================

  "form.state"() {
    this.form.city = "";
  },
  },

  mounted() {
    this.loadUsers();
    this.loadStats();
  },

  methods: {
    // ================================
    // COMMON
    // ================================

    getInitials(name) {
      if (!name) return "U";

      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
    },

    formatDate(date) {
      if (!date) return "—";

      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    // ================================
    // LOAD USERS
    // ================================

    async loadUsers() {
      const res = await getUsersApi({
        page: this.currentPage,
        limit: this.perPage,
        search: this.search,
        verification: this.verificationFilter,
        signup_mode: this.signupFilter,
        city: this.cityFilter,
      });

      if (res && res.status) {
        this.users = Array.isArray(res.users)
          ? res.users
          : [];

        this.totalUsers = Number(res.total || 0);
        this.totalPages = Number(res.totalPages || 0);
      } else {
        this.users = [];
        this.totalUsers = 0;
        this.totalPages = 0;
      }
    },

    // ================================
    // LOAD STATS
    // ================================

    async loadStats() {
      const res = await getUserStatsApi();

      if (res && res.status) {
        this.totalUsers = Number(res.totalUsers || 0);

        this.verifiedUsers = Number(
          res.verifiedUsers || 0
        );

        this.creatorUsers = Number(
          res.creatorUsers || 0
        );

        this.newUsersThisMonth = Number(
          res.newUsersThisMonth || 0
        );
      }
    },

    // ================================
    // PAGINATION
    // ================================

    async changePage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }

      this.currentPage = page;

      await this.loadUsers();
    },

    // ================================
    // FILTER RESET
    // ================================

    resetFilters() {
      this.search = "";
      this.verificationFilter = "";
      this.signupFilter = "";
      this.cityFilter = "";
      this.currentPage = 1;

      this.loadUsers();
    },

    // ================================
    // FORM
    // ================================

    getEmptyForm() {
      return {
        user_id: null,
        name: "",
        mobile: "",
        email: "",
        image: "",
        isVerified: false,
        signup_mode: "",
        youtube_channel: "",
        insta_link: "",
        portfolio_link: "",
        address: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
      };
    },

    openAddModal() {
      this.isEditing = false;
      this.form = this.getEmptyForm();
      this.showFormModal = true;
    },

    closeFormModal() {
      this.showFormModal = false;
      this.isEditing = false;
      this.form = this.getEmptyForm();
    },

    // ================================
    // EDIT USER
    // ================================

    async editUser(user) {
      const res = await getUserApi(user.user_id);

      if (!res || !res.status) {
        alert(
          res?.success || "Unable to fetch user."
        );
        return;
      }

      this.isEditing = true;

      this.form = {
        ...this.getEmptyForm(),
        ...res.user,
      };

      this.showFormModal = true;
    },

    // ================================
    // SAVE USER
    // ================================

    async saveUser() {
      if (!this.form.name || !this.form.name.trim()) {
        alert("Please enter user name.");
        return;
      }

      const email = String(this.form.email || "").trim();
      const mobile = String(this.form.mobile || "").trim();
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobilePattern = /^[0-9]{7,15}$/;
      const urlFields = [
        ["image", "Image"],
        ["youtube_channel", "YouTube channel"],
        ["insta_link", "Instagram profile"],
        ["portfolio_link", "Portfolio"],
      ];

      if (email && !emailPattern.test(email)) {
        alert("Enter a valid email address.");
        return;
      }

      if (mobile && !mobilePattern.test(mobile)) {
        alert("Enter a valid mobile number using 7 to 15 digits.");
        return;
      }

      for (const [field, label] of urlFields) {
        const value = String(this.form[field] || "").trim();

        if (!value) {
          continue;
        }

        try {
          const url = new URL(value);

          if (!["http:", "https:"].includes(url.protocol)) {
            throw new Error("Invalid protocol");
          }
        } catch (error) {
          alert(`${label} must be a valid http:// or https:// URL.`);
          return;
        }
      }

      let res;

      if (this.isEditing) {
        res = await updateUserApi(this.form);
      } else {
        res = await createUserApi(this.form);
      }

      if (res && res.status) {
        alert(
          res.success ||
            (
              this.isEditing
                ? "User updated successfully."
                : "User created successfully."
            )
        );

        this.closeFormModal();

        await this.loadUsers();
        await this.loadStats();

        return;
      }

      alert(
        res?.success ||
          (
            this.isEditing
              ? "User update failed."
              : "User creation failed."
          )
      );
    },

    // ================================
    // VIEW USER
    // ================================

    async viewUser(user) {
      const res = await getUserApi(user.user_id);

      if (!res || !res.status) {
        alert(
          res?.success || "Unable to fetch user."
        );
        return;
      }

      this.selectedUser = res.user;
      this.showViewModal = true;
    },

    // ================================
    // DELETE
    // ================================

    confirmDelete(user) {
      this.selectedUser = user;
      this.showDeleteModal = true;
    },

    async deleteUser() {
      if (!this.selectedUser) {
        return;
      }

      const userId = this.selectedUser.user_id;

      const res = await deleteUserApi(userId);

      if (res && res.status) {
        alert(
          res.success ||
            "User deleted successfully."
        );

        this.selectedUser = null;
        this.showDeleteModal = false;

        // If last user on current page was deleted
        if (
          this.users.length === 1 &&
          this.currentPage > 1
        ) {
          this.currentPage--;
        }

        await this.loadUsers();
        await this.loadStats();

        return;
      }

      alert(
        res?.success ||
          "User deletion failed."
      );
    },
  },
};
</script>

<style scoped>
.users-wrapper {
  padding: 24px;
  background: #f8fafc;
  min-height: calc(100vh - 70px);
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 25px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: white;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: white;
  color: #334155;
  border-color: #e2e8f0;
}

.secondary-btn:hover {
  background: #f8fafc;
}

.reset-btn {
  background: white;
  color: #475569;
  border-color: #e2e8f0;
}

.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: white;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 17px;
  display: flex;
  align-items: center;
  gap: 13px;
}

.stat-icon {
  width: 43px;
  height: 43px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-card span {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-card strong {
  font-size: 21px;
}

/* Card */
.content-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 360px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 13px;
  color: #334155;
  background: white;
}

.search-box input {
  width: 100%;
  padding: 0 13px 0 39px;
}

.filter-select {
  min-width: 145px;
  padding: 0 12px;
}

.search-box input:focus,
.filter-select:focus,
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #93c5fd;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1000px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: left;
  padding: 13px 16px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

td {
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 13px;
}

tbody tr:hover {
  background: #fafcff;
}

.actions-column {
  text-align: right;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 39px;
  height: 39px;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details strong {
  display: block;
  font-size: 13px;
  margin-bottom: 3px;
}

.user-details small {
  color: #94a3b8;
  font-size: 11px;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.contact-info span {
  color: #475569;
  white-space: nowrap;
  font-size: 12px;
}

.contact-info i {
  color: #94a3b8;
  margin-right: 5px;
}

.signup-badge {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
}

.location-info strong {
  display: block;
  font-size: 12px;
  margin-bottom: 3px;
}

.location-info small {
  color: #94a3b8;
  font-size: 11px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.verified {
  background: #f0fdf4;
  color: #15803d;
}

.status-badge.unverified {
  background: #fff7ed;
  color: #c2410c;
}

.date-text {
  color: #64748b;
  font-size: 12px;
  white-space: nowrap;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: 0.2s;
}

.icon-btn.view {
  color: #64748b;
}

.icon-btn.view:hover {
  background: #f1f5f9;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.edit:hover {
  background: #eff6ff;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn.delete:hover {
  background: #fef2f2;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px !important;
  color: #94a3b8;
}

.empty-state i {
  font-size: 42px;
}

.empty-state h3 {
  margin: 10px 0 4px;
  color: #334155;
  font-size: 15px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 31px;
  height: 31px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: #475569;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.view-modal {
  width: 700px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header h2 {
  margin: 0 0 4px;
  font-size: 19px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: #f8fafc;
  border-radius: 7px;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}

/* Form */
.profile-upload,
.view-profile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-avatar {
  width: 65px;
  height: 65px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 19px;
  font-weight: 700;
}

.large-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-upload strong {
  font-size: 13px;
}

.profile-upload p,
.view-profile p {
  margin: 4px 0 0;
  color: #94a3b8;
  font-size: 12px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 11px;
  outline: none;
  font-size: 13px;
  color: #334155;
  font-family: inherit;
}

.form-group input,
.form-group select {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
}

.checkbox-group {
  margin-top: 2px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input {
  width: 16px;
  height: 16px;
}

/* View */
.view-profile h3 {
  margin: 0 0 3px;
  font-size: 18px;
}

.detail-section {
  margin-bottom: 22px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h4 {
  margin: 0 0 12px;
  font-size: 13px;
  color: #334155;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.detail-item {
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.detail-item span {
  display: block;
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  display: block;
  color: #334155;
  font-size: 12px;
  word-break: break-word;
}

.social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.social-links a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 11px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  color: #2563eb;
  text-decoration: none;
  font-size: 12px;
}

.social-links a:hover {
  background: #eff6ff;
}

.no-data {
  color: #94a3b8;
  font-size: 12px;
}

/* Delete */
.delete-modal {
  width: 400px;
  max-width: 100%;
  background: white;
  border-radius: 14px;
  padding: 28px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 19px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 22px;
  display: flex;
  justify-content: center;
  gap: 9px;
}

/* Responsive */
@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    max-width: none;
    flex-basis: 100%;
  }
}

@media (max-width: 700px) {
  .users-wrapper {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    gap: 15px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header .primary-btn {
    white-space: nowrap;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-select,
  .reset-btn {
    width: 100%;
  }

  .form-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>